import React, {
  useState,
  useContext,
  useCallback,
  useLayoutEffect,
} from 'react';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import { setTheme, getWidth } from '../../../helpers';
import ThemeContext from '../../../contexts/ThemeContext';
import { rgba } from 'polished';

export const Toggle = ({
  defaultLabel = 'Light',
  defaultIcon,
  toggledLabel = 'Dark',
  toggledIcon,
  auth,
  ...props
}) => {
  const [themeName, setThemeName] = useContext(ThemeContext);
  const [width, setWidth] = useState(window.innerWidth);

  let showFullToggle;
  if (auth) {
    showFullToggle = width < 1024;
  } else {
    showFullToggle = width < 768;
  }
  const isDesktopLayout = !showFullToggle;

  const toggleTheme = useCallback(
    (e) => {
      e.preventDefault();
      const newTheme = themeName === 'light' ? 'dark' : 'light';

      setThemeName(newTheme);
      setTheme(newTheme);
    },
    [themeName, setThemeName]
  );

  useLayoutEffect(() => {
    return getWidth(setWidth);
  }, []);

  return (
    <ClicToggle
      onClick={(event) => toggleTheme(event)}
      auth={auth}
      isDesktop={isDesktopLayout}
    >
      {showFullToggle ? (
        <>
          <Span className={themeName === 'light' && 'active'}>
            {defaultIcon}
            {defaultLabel}
          </Span>
          <Span className={themeName === 'dark' && 'active'}>
            {toggledIcon}
            {toggledLabel}
          </Span>
        </>
      ) : (
        <>
          {themeName === 'light' ? (
            <Span auth={auth} isDesktop={isDesktopLayout}>
              {toggledIcon}
            </Span>
          ) : (
            <Span auth={auth} isDesktop={isDesktopLayout}>
              {defaultIcon}
            </Span>
          )}
        </>
      )}
    </ClicToggle>
  );
};

Toggle.propTypes = {
  defaultLabel: PropTypes.string,
  defaultIcon: PropTypes.object,
  toggledLabel: PropTypes.string,
  toggledIcon: PropTypes.object,
};

const ClicToggle = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  padding: 4px 3px;
  background: ${(props) =>
    props.theme.name === 'light'
      ? `${props.theme.color.grey5}`
      : `${props.theme.color.black75}`};
  border: none;
  border-radius: 12px;
  cursor: pointer;
  ${(props) =>
    props.isDesktop &&
    css`
      background: ${(props) =>
        props.theme.name === 'light'
          ? `${props.theme.color.white50}`
          : `${props.theme.color.black50}`};
      padding: 0;
      width: 32px;
      height: 32px;
      border-radius: 50%;
      justify-content: center;

      &:hover {
        ${Span} {
          border: 1px solid
            ${props.theme.name === 'light'
              ? `${rgba(props.theme.color.blackFull, 0.65)}`
              : `${rgba(props.theme.color.whiteFull, 0.65)}`};
        }
        svg path {
          fill: ${props.theme.name === 'light'
            ? props.theme.color.blackFull
            : props.theme.color.whiteFull};
        }
      }

      &:active {
        background: ${props.theme.name === 'light'
          ? props.theme.color.white50
          : props.theme.color.black50};
        ${Span} {
          border: 1px solid
            ${props.theme.name === 'light'
              ? props.theme.color.black10
              : props.theme.color.white10};
        }
        svg path {
          fill: ${props.theme.name === 'light'
            ? props.theme.color.black90
            : props.theme.color.white90};
        }
      }

      &:focus-visible {
        background: ${props.theme.name === 'light'
          ? props.theme.color.white50
          : props.theme.color.black50};
        ${Span} {
          border: 1px solid transparent;
        }
        outline: 2px solid
          ${props.theme.name === 'light'
            ? props.theme.color.statusLightFocus
            : props.theme.color.statusDarkFocus};

        svg path {
          fill: ${props.theme.name === 'light'
            ? props.theme.color.blackFull
            : props.theme.color.whiteFull};
        }
      }
      &:disabled,
      &[disabled] {
        background: ${props.theme.name === 'light'
          ? props.theme.color.white50
          : `${rgba(props.theme.color.blackFull, 0.25)}`};
        pointer-events: none;
        ${Span} {
          border: 1px solid
            ${props.theme.name === 'light'
              ? `${(props.theme.color.blackFull, 0.15)}`
              : props.theme.color.white50};
        }

        svg path {
          fill: ${props.theme.name === 'light'
            ? props.theme.color.black75
            : props.theme.color.white75};
        }
      }
    `}
`;

const Span = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px 23px;
  border-radius: 12px;
  font-size: 16px;
  line-height: 24px;
  flex: 1;
  color: ${(props) =>
    props.theme.name === 'light'
      ? `${props.theme.color.grey75}`
      : `${props.theme.color.grey50}`};
  svg {
    margin-right: 8px;
    > path {
      fill: ${(props) =>
        props.theme.name === 'light'
          ? `${props.theme.color.grey75}`
          : `${props.theme.color.grey50}`};
    }
  }
  ${(props) =>
    props.isDesktop &&
    css`
      display: flex;
      align-items: center;
      margin: 6px 0;
      padding: 4px;
      border: none;
      border-radius: 50%;
      cursor: pointer;
      background: ${props.theme.name === 'light'
        ? props.theme.color.white50
        : props.theme.color.black50};
      border: 1px solid
        ${props.theme.name === 'light'
          ? `${rgba(props.theme.color.blackFull, 0.35)}`
          : props.theme.color.white50};

      svg {
        margin-right: 0;
        width: 100%;
        height: 100%;
        path {
          fill: ${props.theme.name === 'light'
            ? props.theme.color.black90
            : props.theme.color.white90};
        }
      }
    `};
  &.active {
    ${(props) => props.theme.fontBold};
    font-weight: 500;
    background: ${(props) =>
      props.theme.name === 'light'
        ? `${props.theme.color.whiteFull}`
        : `${props.theme.color.black90}`};

    box-shadow: ${(props) =>
      props.theme.name === 'light'
        ? '0px 4px 8px rgba(0, 0, 0, 0.05), 0px 2px 4px rgba(0, 0, 0, 0.05);'
        : '0px 4px 8px rgba(0, 0, 0, 0.05), 0px 2px 4px rgba(0, 0, 0, 0.05);'};
    color: ${(props) =>
      props.theme.name === 'light'
        ? `${props.theme.color.black90}`
        : `${props.theme.color.whiteFull}`};
    svg > path {
      fill: ${(props) =>
        props.theme.name === 'light'
          ? `${props.theme.color.black90}`
          : `${props.theme.color.whiteFull}`};
    }
  }
`;
